import { Box, Typography, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLayoutEffect, useRef, useState } from 'react';
import { InfoIcon, LongArrowDown, LongArrowUp } from '../../CommonIcons/Icons';
import AutoScaleNumber from '../AutoScaleNumber';
import LoaderComponent from 'components/Loader';
import { STATUS_LABELS } from 'components/PipelineRunStatus/utils/pipelineChartUtils';

interface TileSummaryItem {
  total: number,
  difference: number,
  trend: string
}

export interface SummaryTilesConfigItem {
  key: string;
  label: string;
  summaryKey: string;
}

export interface SummaryTilesProps {
  summary: { [key: string]: TileSummaryItem } | null;
  activeTile: string;
  onTileClick: (key: string) => void;
  isLoading: boolean;
  isScheduledLoading?: boolean;
  isCustomStatusLoading?: boolean;
  tilesConfig: SummaryTilesConfigItem[];
  tooltipLabel: string | ((tileKey: string) => string);
  tileLabel: string | ((tileKey: string) => string);
  gridColumns?: { xs?: number; sm?: number; md?: number; lg?: number };
  showTrend?: boolean;
}

const SummaryTiles = ({ summary, activeTile, onTileClick, isLoading, isScheduledLoading = false, isCustomStatusLoading = false, tilesConfig, tooltipLabel, tileLabel, gridColumns, showTrend = true }: SummaryTilesProps) => {
  const theme = useTheme();
  const p = theme.palette;
  const numberRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [sharedFontSize, setSharedFontSize] = useState(48);

  const maxTotal = summary
    ? Math.max(...tilesConfig.map((tile) => summary[tile.summaryKey]?.total ?? 0))
    : 0;

  useLayoutEffect(() => {
    const gridElement = gridRef.current;
    if (!gridElement) return;

    const recalcFontSize = () => {
      const widths = tilesConfig
        .map((tile) => numberRefs.current[tile.key]?.offsetWidth)
        .filter((width): width is number => typeof width === 'number' && width > 0);

      if (widths.length === 0) return;

      const minWidth = Math.min(...widths);
      const measurement = document.createElement('span');
      Object.assign(measurement.style, {
        position: 'absolute',
        visibility: 'hidden',
        whiteSpace: 'nowrap',
        fontWeight: '500',
        lineHeight: '1.1',
        fontSize: '48px',
        left: '-9999px',
        top: '-9999px',
      });
      measurement.textContent = maxTotal.toLocaleString('en-US');
      document.body.appendChild(measurement);

      let size = 48;
      while (measurement.offsetWidth > minWidth && size > 24) {
        size -= 1;
        measurement.style.fontSize = `${size}px`;
      }

      measurement.remove();
      setSharedFontSize(size);
    };

    const observer = new ResizeObserver(recalcFontSize);
    observer.observe(gridElement);
    recalcFontSize();

    return () => observer.disconnect();
  }, [maxTotal, tilesConfig.length, summary]);

  return (
    <Box ref={gridRef} sx={{
      flex: 1,
      minWidth: 0,
      display: 'grid',
      gap: 1,
      gridTemplateColumns: {
        xs: `repeat(${gridColumns?.xs ?? 2}, 1fr)`,              
        sm: `repeat(${gridColumns?.sm ?? 4}, 1fr)`,    
        md: `repeat(${gridColumns?.md ?? tilesConfig.length}, minmax(0,1fr))`,
        lg: `repeat(${gridColumns?.lg ?? tilesConfig.length}, minmax(0,1fr))`,
      }}}
    >
      {tilesConfig.map((tile) => {
        const data = summary?.[tile.summaryKey];
        const isActive = activeTile === tile.key;
        const totalValue = data?.total ?? 0;
        const isClickable = totalValue > 0;

        const isScheduledTile = tile?.key?.toLowerCase() === STATUS_LABELS.SCHEDULED;
        const isCustomStatusTile = tile?.key?.toLowerCase() === STATUS_LABELS.HUNG || tile?.key?.toLowerCase() === STATUS_LABELS.LONG_RUNNING;
        const showLoader =
          (isLoading && !isScheduledTile) ||
          (isScheduledLoading && isScheduledTile) || (isCustomStatusTile && isCustomStatusLoading);
        const isDimmed = !isClickable && !showLoader;

        const trend = data?.trend;
        const trendUp = trend === 'INCREASED';
        const trendDown = trend === 'DECREASED';

        const textColor = isActive ? p.primary.main : p.white.main;
        const bgColor = isActive ? p.white.main : p.primary.main;
        const iconSize = 'clamp(12px, 1vw, 18px)';
        const computedTileLabel =
          typeof tileLabel === 'function'
            ? tileLabel(tile.key)
            : tileLabel;

        return (
          <Box
            key={tile.key}
            onClick={() => isClickable && onTileClick(tile.key)}
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 },
              gap: 1,
              display: 'flex',
              cursor: isClickable ? 'pointer' : 'default',
              opacity: isDimmed ? 0.75 : 1,
              flexDirection: 'column',
              alignItems: 'flex-start',
              backgroundColor: bgColor,
              borderRadius: '0px 4px 4px 4px',
              transition: 'background-color 0.2s ease',
              '&:hover': {
                backgroundColor: p.white.main,
                '& *': { color: `${p.primary.main} !important` },
                '& svg path': { fill: `${p.primary.main} !important` }
              }
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <Typography
                variant="body1"
                sx={{ color: textColor, fontWeight: 500, lineHeight: 1.2 }}
              >
                {tile.label}
              </Typography>
              <Tooltip
                arrow
                title={`${tile.label} ${typeof tooltipLabel === 'function' ? tooltipLabel(tile.key) : tooltipLabel}`}
                placement="top"
                enterDelay={500}
              >
                <Box sx={{ cursor: 'auto', mt: '1px', flexShrink: 0 }}>
                  <InfoIcon size={20} color={p.info.main} />
                </Box>
              </Tooltip>
            </Box>
            {showLoader ? (
              <LoaderComponent />
            ) : (
              <Box ref={(el) => { numberRefs.current[tile.key] = el as HTMLDivElement | null; }} sx={{ width: '100%' }}>
                <AutoScaleNumber
                  value={data?.total ?? 0}
                  maxValue={maxTotal}
                  color={textColor}
                  fontSize={sharedFontSize}
                />
              </Box>
            )}
            {showTrend && computedTileLabel !== '' && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                ml: (!isScheduledTile && String(Math.abs(data?.difference ?? 0)).length > 4) ? '-3px' : '0px',
              }}>
                {(trendUp && !isScheduledTile) && <LongArrowUp color={textColor} size={iconSize} />}
                {(trendDown && !isScheduledTile) && <LongArrowDown color={textColor}  size={iconSize}/>}
                {isScheduledTile ? (
                  <Typography
                    variant="body1"
                    sx={{
                      color: textColor,
                      fontSize: 'clamp(12px, 1vw, 16px)',
                      lineHeight: 1
                    }}
                  >
                    {computedTileLabel}
                  </Typography>
                ) : (
                  <>
                    <Typography
                      variant="body1"
                      sx={{
                        color: textColor,
                        lineHeight: 1,
                        fontSize: 'clamp(12px, 1vw, 16px)',
                      }}
                    >
                      {showLoader ? '...' : Math.abs(data?.difference ?? 0).toLocaleString('en-US')}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: textColor,
                        lineHeight: 1,
                        fontSize: 'clamp(12px, 1vw, 16px)',
                      }}
                    >
                      {computedTileLabel}
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default SummaryTiles;
